import React, { FC } from "react";
import { NutritionTargetType } from "@growth-machine-llc/stridist-api-client";
import NutritionAccordion from "../common/NutritionAccordion";
import { BoundNutrients } from "../common/BoundNutrients";
import { Box } from "@mui/material";
import { nutritionTargetsArray } from "../../../constants";

export interface NutrientInfo {
  value: number;
  dirty: boolean;
}

export interface TargetInfo {
  protein: NutrientInfo;
  carbohydrate: NutrientInfo;
  fat: NutrientInfo;
  calories: NutrientInfo;
}

export interface TargetViewProps {
  state: Record<NutritionTargetType, TargetInfo>;
  setState: React.Dispatch<
    React.SetStateAction<Record<NutritionTargetType, TargetInfo>>
  >;
}

const TargetsView: FC<TargetViewProps> = ({ state, setState }) => {
  const firstTargetId = Object.keys(state)[0];
  const [expandedId, setExpandedId] = React.useState(firstTargetId);

  React.useEffect(() => {
    if (firstTargetId) {
      setExpandedId(firstTargetId);
    }
  }, [firstTargetId]);

  const setProteinValue = (targetId: NutritionTargetType, value: number) => {
    setState((prevState) => {
      const target = prevState[targetId];
      return {
        ...prevState,
        [targetId]: {
          ...target,
          protein: { ...target.protein, value },
        },
      };
    });
  };

  const setCarbohydrateValue = (
    targetId: NutritionTargetType,
    value: number,
  ) => {
    setState((prevState) => {
      const target = prevState[targetId];
      return {
        ...prevState,
        [targetId]: {
          ...target,
          carbohydrate: { ...target.carbohydrate, value },
        },
      };
    });
  };

  const setFatValue = (targetId: NutritionTargetType, value: number) => {
    setState((prevState) => {
      const target = prevState[targetId];
      return {
        ...prevState,
        [targetId]: {
          ...target,
          fat: { ...target.fat, value },
        },
      };
    });
  };

  const setCaloriesValue = (targetId: NutritionTargetType, value: number) => {
    setState((prevState) => {
      const target = prevState[targetId];
      return {
        ...prevState,
        [targetId]: {
          ...target,
          calories: { ...target.calories, value },
        },
      };
    });
  };

  const setProteinDirty = (targetId: NutritionTargetType, dirty: boolean) => {
    setState((prevState) => {
      const target = prevState[targetId];
      return {
        ...prevState,
        [targetId]: {
          ...target,
          protein: { ...target.protein, dirty },
        },
      };
    });
  };

  const setCarbohydrateDirty = (
    targetId: NutritionTargetType,
    dirty: boolean,
  ) => {
    setState((prevState) => {
      const target = prevState[targetId];
      return {
        ...prevState,
        [targetId]: {
          ...target,
          carbohydrate: { ...target.carbohydrate, dirty },
        },
      };
    });
  };

  const setFatDirty = (targetId: NutritionTargetType, dirty: boolean) => {
    setState((prevState) => {
      const target = prevState[targetId];
      return {
        ...prevState,
        [targetId]: {
          ...target,
          fat: { ...target.fat, dirty },
        },
      };
    });
  };

  const setCaloriesDirty = (targetId: NutritionTargetType, dirty: boolean) => {
    setState((prevState) => {
      const target = prevState[targetId];
      return {
        ...prevState,
        [targetId]: {
          ...target,
          calories: { ...target.calories, dirty },
        },
      };
    });
  };

  return (
    <>
      {Object.entries(state).map(([type, target]) => (
        <NutritionAccordion
          key={type}
          expanded={expandedId === type}
          onChange={() => setExpandedId(type)}
          calories={target.calories.value}
          protein={target.protein.value}
          carbs={target.carbohydrate.value}
          fat={target.fat.value}
          title={nutritionTargetsArray.find((t) => t.value === type)?.label}
          showSummaryOnExpanded={Object.entries(state).length > 1}
        >
          <Box>
            <BoundNutrients
              protein={{
                value: target.protein.value,
                dirty: target.protein.dirty,
                setValue: (value) =>
                  setProteinValue(
                    type as unknown as NutritionTargetType,
                    value,
                  ),
                setDirty: (dirty) =>
                  setProteinDirty(
                    type as unknown as NutritionTargetType,
                    dirty,
                  ),
              }}
              carbohydrate={{
                value: target.carbohydrate.value,
                dirty: target.carbohydrate.dirty,
                setValue: (value) =>
                  setCarbohydrateValue(
                    type as unknown as NutritionTargetType,
                    value,
                  ),
                setDirty: (dirty) =>
                  setCarbohydrateDirty(
                    type as unknown as NutritionTargetType,
                    dirty,
                  ),
              }}
              fat={{
                value: target.fat.value,
                dirty: target.fat.dirty,
                setValue: (value) =>
                  setFatValue(type as unknown as NutritionTargetType, value),
                setDirty: (dirty) =>
                  setFatDirty(type as unknown as NutritionTargetType, dirty),
              }}
              calories={{
                value: target.calories.value,
                dirty: target.calories.dirty,
                setValue: (value) =>
                  setCaloriesValue(
                    type as unknown as NutritionTargetType,
                    value,
                  ),
                setDirty: (dirty) =>
                  setCaloriesDirty(
                    type as unknown as NutritionTargetType,
                    dirty,
                  ),
              }}
              variant="target"
            />
          </Box>
        </NutritionAccordion>
      ))}
    </>
  );
};

export default TargetsView;
